// src/Dashboard/pages/user/Galleries/hooks/useAlbumGalerie.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useUser } from "../../../../../pages/Home/UserContext";
import { getRoleFromRoleId } from "../../../../../utils/roleHelper";

import { getGalleryById } from "../../../../../services/gallerieServices";
import {
  movePhoto as apiMovePhoto,
  togglePhotoCover,
} from "../../../../../services/photoServices";

import { PAGE_SIZE, sortPhotos } from "../utils/galleriesUtils";

export function useAlbumGalerie() {
  const navigate = useNavigate();
  const { albumId } = useParams();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isManager = userRole === "admin" || userRole === "enseignant";

  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);

  const [perPage, setPerPage] = useState(PAGE_SIZE);

  const [photoDrawerOpen, setPhotoDrawerOpen] = useState(false);
  const [photoDrawerMode, setPhotoDrawerMode] = useState("create"); // create | edit
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const [removeDrawerOpen, setRemoveDrawerOpen] = useState(false);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [draggingIndex, setDraggingIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const dndRef = useRef({ from: null });

  const fetchGallery = useCallback(async () => {
  try {
    setLoading(true);

    const response = await getGalleryById(Number(albumId));

    if (response && response.id) {
      setGallery(response);
    } else {
      setGallery(null);
    }
  } catch (err) {
    console.error("Erreur chargement album :", err);
    setGallery(null);
    toast.error("Impossible de charger l’album.");
  } finally {
    setLoading(false);
  }
}, [albumId]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const photos = useMemo(() => {
    return sortPhotos(gallery?.photos || []);
  }, [gallery]);

  const visiblePhotos = useMemo(() => {
    return photos.slice(0, perPage);
  }, [photos, perPage]);

  const stats = useMemo(() => {
    const total = photos.length;
    const active = photos.filter((photo) => photo?.is_active !== false).length;
    const hidden = total - active;
    const cover = photos.filter((photo) => photo?.is_cover === true).length;

    return {
      total,
      active,
      hidden,
      cover,
    };
  }, [photos]);

  const openCreatePhotoDrawer = useCallback(() => {
    setSelectedPhoto(null);
    setPhotoDrawerMode("create");
    setPhotoDrawerOpen(true);
  }, []);

  const openEditPhotoDrawer = useCallback((photo) => {
    setSelectedPhoto(photo);
    setPhotoDrawerMode("edit");
    setPhotoDrawerOpen(true);
  }, []);

  const closePhotoDrawer = useCallback(() => {
    setPhotoDrawerOpen(false);
    setSelectedPhoto(null);
    setPhotoDrawerMode("create");
  }, []);

  const handlePhotoSaved = useCallback(async () => {
    await fetchGallery();
    closePhotoDrawer();
  }, [fetchGallery, closePhotoDrawer]);

  const openRemoveDrawer = useCallback(() => {
    setRemoveDrawerOpen(true);
  }, []);

  const closeRemoveDrawer = useCallback(() => {
    setRemoveDrawerOpen(false);
  }, []);

  const handlePhotoDeleted = useCallback(async () => {
    await fetchGallery();
    closeRemoveDrawer();
  }, [fetchGallery, closeRemoveDrawer]);

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const copyShare = useCallback(async () => {
    try {
      const url = `${window.location.origin}${window.location.pathname}`;
      await navigator.clipboard.writeText(url);
      toast.success("Lien de l’album copié.");
    } catch {
      toast.error("Impossible de copier le lien.");
    }
  }, []);

  const handleToggleCover = useCallback(
    async (event, photo) => {
      event?.stopPropagation?.();

      if (!isManager || !photo?.id) return;

      try {
        setGallery((current) => {
          if (!current) return current;

          const nextPhotos = Array.isArray(current.photos)
            ? current.photos.map((item) => ({
                ...item,
                is_cover:
                  item.id === photo.id
                    ? photo.is_cover
                      ? null
                      : true
                    : null,
              }))
            : [];

          return {
            ...current,
            photos: nextPhotos,
          };
        });

        await togglePhotoCover(photo.id, { gallerieId: albumId });
        await fetchGallery();

        toast.success(
          photo.is_cover
            ? "Couverture retirée."
            : "Photo définie comme couverture."
        );
      } catch (err) {
        console.error("Erreur couverture :", err);
        toast.error("Échec du changement de couverture.");
        fetchGallery();
      }
    },
    [isManager, albumId, fetchGallery]
  );

  const onDragStart = useCallback(
    (event, index) => {
      if (!isManager) return;

      dndRef.current.from = index;
      setDraggingIndex(index);

      event.dataTransfer.effectAllowed = "move";

      try {
        event.dataTransfer.setData("text/plain", String(index));
      } catch {}
    },
    [isManager]
  );

  const onDragOver = useCallback(
    (event, index) => {
      if (!isManager) return;

      event.preventDefault();
      setOverIndex(index);
      event.dataTransfer.dropEffect = "move";
    },
    [isManager]
  );

  const onDragLeave = useCallback(() => {
    setOverIndex(null);
  }, []);

  const onDrop = useCallback(
    async (event, toIndex) => {
      if (!isManager) return;

      event.preventDefault();

      const fromIndex = (() => {
        try {
          return parseInt(event.dataTransfer.getData("text/plain"), 10);
        } catch {
          return dndRef.current.from;
        }
      })();

      setOverIndex(null);
      setDraggingIndex(null);

      if (Number.isNaN(fromIndex) || fromIndex === null) return;
      if (fromIndex === toIndex) return;

      try {
        const moved = visiblePhotos[fromIndex];

        if (!moved) return;

        await apiMovePhoto(moved.id, toIndex, { gallerieId: albumId });
        await fetchGallery();

        toast.success("Ordre des photos mis à jour.");
      } catch (err) {
        console.error("Erreur déplacement photo :", err);
        toast.error("Échec du déplacement.");
        fetchGallery();
      }
    },
    [isManager, visiblePhotos, albumId, fetchGallery]
  );

  const loadMore = useCallback(() => {
    setPerPage((previous) => previous + PAGE_SIZE);
  }, []);

  return {
    navigate,
    gallery,
    loading,
    photos,
    visiblePhotos,
    stats,
    isManager,

    perPage,
    loadMore,

    photoDrawerOpen,
    photoDrawerMode,
    selectedPhoto,
    openCreatePhotoDrawer,
    openEditPhotoDrawer,
    closePhotoDrawer,
    handlePhotoSaved,

    removeDrawerOpen,
    openRemoveDrawer,
    closeRemoveDrawer,
    handlePhotoDeleted,

    lightboxOpen,
    lightboxIndex,
    openLightbox,
    closeLightbox,

    draggingIndex,
    overIndex,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,

    handleToggleCover,
    copyShare,
    refresh: fetchGallery,
  };
}